import { motion } from 'framer-motion';
import { Heart, Shield, Gift, TrendingUp } from 'lucide-react';

const impactItems = [
  {
    icon: Gift,
    title: 'Food for Families',
    description: '$25 provides groceries for a family for one week'
  },
  {
    icon: Heart,
    title: 'Youth Programs',
    description: '$50 supports a month of youth mentorship activities'
  },
  {
    icon: TrendingUp,
    title: 'Future Farm',
    description: '$100 contributes to our organic farming initiative'
  }
];

export const Donate = () => {
  return (
    <div data-testid="donate-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-gold uppercase tracking-[0.3em] text-sm mb-4">Support Our Ministry</p>
            <h1 className="font-cinzel text-4xl sm:text-5xl lg:text-6xl text-foreground mb-6">
              Give <span className="text-gold">Generously</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Your generosity fuels our mission to spread the Word and serve our community. Every gift makes an eternal impact.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Donation Form Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Left Side - Impact Info */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="font-cinzel text-2xl text-gold mb-6">Your Impact</h2>
                <div className="space-y-6">
                  {impactItems.map((item, index) => (
                    <div key={item.title} className="flex gap-4" data-testid={`impact-${index}`}>
                      <div className="w-12 h-12 flex-shrink-0 rounded-full bg-gold/10 flex items-center justify-center">
                        <item.icon className="text-gold" size={24} />
                      </div>
                      <div>
                        <h3 className="text-foreground font-semibold mb-1">{item.title}</h3>
                        <p className="text-muted-foreground text-sm">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 p-4 bg-muted/30 border border-border rounded-sm">
                  <div className="flex items-start gap-3">
                    <Shield className="text-gold flex-shrink-0 mt-1" size={20} />
                    <div>
                      <p className="text-foreground font-semibold text-sm mb-1">Secure & Tax Deductible</p>
                      <p className="text-muted-foreground text-xs">
                        True Believer is a 501(c)(3) nonprofit. All donations are tax-deductible to the fullest extent allowed by law.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Right Side - Zeffy Embed */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-3"
            >
              <div 
                className="zeffy-container border border-gold rounded-sm overflow-hidden gold-glow bg-white"
                data-testid="zeffy-embed-container"
              >
                <iframe
                  title="Donation form powered by Zeffy"
                  src="https://www.zeffy.com/en-US/donation-form/9a15f717-9dd2-4d6a-98ca-b2e4c12cc8e3"
                  style={{ width: '100%', minHeight: '700px', border: 'none' }}
                  allowPaymentRequest
                  allow="payment"
                />
              </div>
              <p className="text-center text-muted-foreground text-xs mt-4">
                Powered by Zeffy — 100% of your donation goes to the ministry
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Other Ways to Give */}
      <section className="py-24 bg-card">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-cinzel text-3xl text-gold mb-6">Other Ways to Give</h2>
            <p className="text-muted-foreground mb-8">
              In addition to online donations, we accept contributions through:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="card-dark">
                <h3 className="font-cinzel text-lg text-foreground mb-2">Cash App</h3>
                <p className="text-gold font-mono">$TrueBelieverFam</p>
              </div>
              <div className="card-dark">
                <h3 className="font-cinzel text-lg text-foreground mb-2">Mail</h3>
                <p className="text-muted-foreground text-sm">Contact us for mailing address</p>
              </div>
              <div className="card-dark">
                <h3 className="font-cinzel text-lg text-foreground mb-2">In Person</h3>
                <p className="text-muted-foreground text-sm">At any of our events</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Thank You Message */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Heart className="text-gold mx-auto mb-4" size={48} />
            <h2 className="font-cinzel text-2xl text-foreground mb-4">Thank You</h2>
            <p className="text-muted-foreground">
              "Each of you should give what you have decided in your heart to give, not reluctantly or under compulsion, for God loves a cheerful giver." — 2 Corinthians 9:7
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
